import { EventEmitter } from 'events';
import { merge, mergeWith, isPlainObject, find } from 'lodash';
import ga from 'react-ga';
import { format } from 'date-fns';
import Dispatcher from 'core/Dispatcher';
import Actions from 'constants/Actions';
import { validate } from 'api';
import InstantBookingStore from 'stores/InstantBookingStore'
import CategoryStore from 'stores/CategoryStore';
import TimeConstant from 'constants/Time';
import ru from 'date-fns/locale/ru';

const countriesConfig = require('../../configs/countries.config.json')
const CHANGE_EVENT = 'change'

let _locations = parseLocations()

const DEFAULT_DATE_FROM = TimeConstant.DefaultDateFrom;
const DEFAULT_DATE_TO = TimeConstant.DefaultDateTo;
const DEFAULT_TIME_FROM = null;
const DEFAULT_TIME_TO = null;

const _dates = {
    calendarStartDate: DEFAULT_DATE_FROM,
    from: DEFAULT_DATE_FROM,
    to: DEFAULT_DATE_TO,
    opened: false,
    errors: {},
}

const _time = {
    from: DEFAULT_TIME_FROM,
    to: DEFAULT_TIME_TO,
}

const _countries = Object.values(countriesConfig).map(country => {
    return {
        value: parseInt(country.id),
        name: country.name,
        label: `${country.name} (${country.original_name})`,
        iso: country.iso,
    };
});

/**
 * @return {Object.<[from|to],  Waypoint>}
 */
function parseLocations(locations = []) {
    var result = {
        from: [],
        to: [],
    }

    for (let i = 0; i <= 1; i++) {
        let group = locations[i] || []
        result.from.push(group[1])
        result.to.push(group[2])
    }

    return result
}

function normalizeLocations(locations) {
    const result = []

    for (let i = 0; i <= 1; i++) {
        let group = {}

        if (locations.from[i] && locations.from[i].text) {
            group['1'] = locations.from[i]
        }

        if (locations.to[i] && locations.to[i].text) {
            group['2'] = locations.to[i]
        }

        result.push(group)
    }

    return result
}

function denormalizeLocations(normalizedLocations, manualCityFull = false) {

    var result = {
        from: [],
        to: [],
    };

    for (let i = 0; i <= 1; i++) {
        if (normalizedLocations[i] && normalizedLocations[i][1]) {
            var location = normalizedLocations[i][1];
            var country = getCountryById(location.country);
            let cityFull = location.city_full || location.city

            if (manualCityFull) {
                const cityFullArray = []

                if (location.region) {
                    cityFullArray.push(location.region)
                }

                if (location.city) {
                    cityFullArray.push(location.city)
                }

                if (location.street) {
                    cityFullArray.push(location.street)
                }

                if (location.home) {
                    cityFullArray.push(location.home)
                }

                if (cityFullArray.length > 0) {
                    cityFull = cityFullArray.join(', ')
                }
            }

            if (Object.keys(country).length > 0) {
                result.from[i] = Object.assign(location, {
                    country: parseInt(location.country, 10),
                    country_name: country.name,
                    city_full: cityFull,
                    city: location.city,
                    text: cityFull,
                    need_calibration: false,
                });
            }
        }

        if (normalizedLocations[i] && normalizedLocations[i][2]) {

            var location = normalizedLocations[i][2];
            var country = getCountryById(location.country);
            let cityFull = location.city_full || location.city

            if (manualCityFull) {
                const cityFullArray = []

                if (location.region) {
                    cityFullArray.push(location.region)
                }

                if (location.city) {
                    cityFullArray.push(location.city)
                }

                if (location.street) {
                    cityFullArray.push(location.street)
                }

                if (location.home) {
                    cityFullArray.push(location.home)
                }

                if (cityFullArray.length > 0) {
                    cityFull = cityFullArray.join(', ')
                }
            }

            if (Object.keys(country).length > 0) {
                result.to[i] = Object.assign(location, {
                    country: parseInt(location.country, 10),
                    country_name: country.name,
                    city_full: cityFull,
                    city: location.city,
                    text: cityFull,
                    need_calibration: false,
                });
            }
        }
    }

    if (result.from) {
        updateLocations({
            destination: 'from',
            waypoints: result.from,
        });
    }

    if (result.to) {
        updateLocations({
            destination: 'to',
            waypoints: result.to,
        });
    }
}

function updateLocations({ destination, waypoints }) {
    _locations[destination] = waypoints;
}

function updateDates({ calendarStartDate, from, to, opened, errors }) {
    merge(_dates, { calendarStartDate, from, to, opened, errors });
}

function updateTime({ from, to }) {
    merge(_time, { from, to });
}

function sendRoutesToGa() {

    var selectedCategory = CategoryStore.getSelected();

    ga.event({
        category: 'fields',
        action: 'fill_from_first_booking',
        label: selectedCategory.name,
        value: 1,
    });

    ga.event({
        category: 'fields',
        action: 'fill_to_first_booking',
        label: selectedCategory.name,
        value: 1,
    });

    var countryFrom = _.find(_countries, (o) => {
        return _locations.from[0].country === o.value;
    });

    ga.event({
        category: 'fields',
        action: `fill_from_country_first_${countryFrom.name}`,
        label: selectedCategory.name,
        value: 1,
    });

    if (_locations.to[0] && _locations.to[0].country) {
        var countryTo = _.find(_countries, (o) => {
            return _locations.to[0].country === o.value;
        });

        ga.event({
            category: 'fields',
            action: `fill_to_country_first_${countryTo.name}`,
            label: selectedCategory.name,
            value: 1,
        });
    }

    if (_locations.from[1]) {
        ga.event({
            category: 'fields',
            action: 'fill_from_second_booking_step_4',
            label: selectedCategory.name,
            value: 1,
        });

        var countryAdditionalFrom = _.find(_countries, (o) => {
            return _locations.from[1].country === o.value;
        });

        ga.event({
            category: 'fields',
            action: `fill_from_country_second_${countryAdditionalFrom.name}`,
            label: selectedCategory.name,
            value: 1,
        });
    }

    if (_locations.to[1]) {
        ga.event({
            category: 'fields',
            action: 'fill_to_second_booking_step_4',
            label: selectedCategory.name,
            value: 1,
        });

        var countryAdditionalTo = _.find(_countries, (o) => {
            return _locations.to[1].country === o.value;
        });

        ga.event({
            category: 'fields',
            action: `fill_to_country_second_${countryAdditionalTo.name}`,
            label: selectedCategory.name,
            value: 1,
        });
    }
}

function sendDatesToGa() {
    if (_dates.from !== DEFAULT_DATE_FROM || _dates.to !== DEFAULT_DATE_TO) {
        ga.event({
            category: 'fields',
            action: 'change_date_booking',
            label: CategoryStore.getSelected().name,
            value: 1,
        });
    }
}

function getCountryById(countryId) {
    return find(_countries, (o) => {
        return parseInt(countryId, 10) === o.value;
    });
}

class RouteStore extends EventEmitter {

    getLocations() {
        return _locations;
    }

    getDates() {
        return _dates;
    }

    getTime() {
        return _time;
    }

    getCountries() {
        return _countries;
    }

    getCountryById(countryId) {
        getCountryById(countryId);
    }

    clearLocations() {
        _locations = parseLocations()
    }

    normalizeState(result) {
        // Собираем объект маршрутов. Главные пункты погрузки и выгрузки добавляем сразу.
        result.locations = [];
        result.locations[0] = _locations[0];
        result.locations = normalizeLocations(_locations);

        result.dates = {}

        if (_dates.from) {
            result.dates.date_from = format(_dates.from, 'YYYY-MM-DD', { locale: ru })
        }

        if (_dates.to) {
            result.dates.date_to = format(_dates.to, 'YYYY-MM-DD', { locale: ru })
        }

        if (_dates.calendarStartDate) {
            result.dates.calendarStartDate = format(_dates.calendarStartDate, 'YYYY-MM-DD', { locale: ru })
        }

        result.time = {};

        const instantBookingPartDay = InstantBookingStore.getPartDay()

        if (instantBookingPartDay.size > 0) {
            result.time.time_from = format(instantBookingPartDay.valueSeq().get(0).dt_start, 'HH:mm', { locale: ru })
        } else if (_time.from) {
            result.time.time_from = TimeConstant.Hours[_time.from].label;
        }

        if (instantBookingPartDay.size > 0) {
            result.time.time_to = format(instantBookingPartDay.valueSeq().get(0).dt_end, 'HH:mm', { locale: ru })
        } else if (_time.to) {
            result.time.time_to = TimeConstant.Hours[_time.to].label;
        }

        if (Object.keys(result.time).length === 0) {
            delete result.time;
        }
    }

    denormalizeState(normalizedState) {
        if (normalizedState.locations) {
            denormalizeLocations(normalizedState.locations, Boolean(normalizedState.price_hash));
        }

        if (normalizedState.dates) {
            updateDates({
                from: normalizedState.dates.date_from ? new Date(Date.parse(normalizedState.dates.date_from)) : DEFAULT_DATE_FROM,
                to: normalizedState.dates.date_to ? new Date(Date.parse(normalizedState.dates.date_to)) : DEFAULT_DATE_TO,
                opened: true,
            });
        }

        if (normalizedState.time) {

            var timeFrom = DEFAULT_TIME_FROM;
            if (normalizedState.time.time_from) {
                var timeObj = TimeConstant.Hours.find(item => {
                    return item.label === normalizedState.time.time_from;
                });

                if (timeObj) {
                    timeFrom = timeObj.value;
                }
            }

            var timeTo = DEFAULT_TIME_TO;
            if (normalizedState.time.time_to) {
                var timeObj = TimeConstant.Hours.find(item => {
                    return item.label === normalizedState.time.time_to;
                });

                if (timeObj) {
                    timeTo = timeObj.value;
                }
            }

            updateTime({
                from: timeFrom,
                to: timeTo,
            });
        }
    }

    validate() {
        return validate().then(
            response => {
                const { locations, dates } = response;
                if (!locations && !dates) {
                    return Promise.resolve();
                }

                const locationsErrors = parseLocations(locations);
                mergeWith(_locations, locationsErrors, function (source, error) {
                    if (!isPlainObject(error) && typeof error !== 'string') return;
                    if (error) {
                        return merge({}, source, { error });
                    }
                });

                const datesErrors = {}

                if (dates && dates.date_from) {
                    datesErrors.from = dates.date_from
                }

                if (dates && dates.date_to) {
                    datesErrors.to = dates.date_to
                }

                mergeWith(_dates.errors, datesErrors, function (source, error) {
                    if (!isPlainObject(error) && typeof error !== 'string') return;
                    return error
                });

                return Promise.reject({
                    locationsErrors,
                    datesErrors,
                });
            }
        );
    }

    /**
     * @param {function} callback
     */
    addChangeListener(callback) {
        this.on(CHANGE_EVENT, callback);
    }

    /**
     * @param {function} callback
     */
    removeChangeListener(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    }

    emitChange() {
        this.emit(CHANGE_EVENT);
    }

    dispatchToken = Dispatcher.register((action) => {
        const payload = action.payload;

        switch (action.type) {
            case Actions.UPDATE_LOCATIONS:
                updateLocations(payload)
                break

            case Actions.UPDATE_DATES:
                updateDates(payload)
                break

            case Actions.UPDATE_TIME:
                updateTime(payload)
                break

            case Actions.SEND_ROUTES_TO_GA:
                sendRoutesToGa()
                break

            case Actions.SEND_DATES_TO_GA:
                sendDatesToGa()
                break

            default:
                return
        }

        this.emitChange();
    });

}

export default new RouteStore();



// WEBPACK FOOTER //
// ./client/stores/RouteStore.js